import { Plus, MagicWand } from "../../ui/Icon/Icon";
import Page from "../Page";
import { ArticleStatus } from "../../__generated__/gql/graphql";
import NewArticleModal from "./components/NewArticleModal/NewArticleModal";
import ArticleTable from "./components/ArticleTable/ArticleTable";
import Badge from "../../ui/Badge/Badge";
import { useState } from "react";
import { graphql } from "../../__generated__/gql";
import { useQuery } from "urql";
import EmptyState from "../../ui/EmptyState/EmptyState";
import useFilters from "../../ui/FilterDrawer/useFilters";
import Tabs from "../../ui/Tabs/Tabs";
import { useDisclosure } from "../../ui/util/useDisclosure";
import styles from "./Articles.module.scss";

const ArticleCountsQuery = graphql(/* GraphQL */ `
  query articleCounts {
    articleCounts(input: { filters: [{ patternIds: null }] }) {
      total
      draft
      generated
      published
      processing
      archived
    }
  }
`);

function Articles() {
  const [opened, { open, close }] = useDisclosure();
  const [tableLoaded, setTableLoaded] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [articleCountsResponse] = useQuery({ query: ArticleCountsQuery });
  const { filterGroups, deleteFilter, addFilter, editFilter, clearFilters } =
    useFilters({});
  const isEmpty =
    articleCountsResponse.data?.articleCounts.total === 0 &&
    articleCountsResponse.data?.articleCounts.archived === 0;

  return (
    <Page
      loading={articleCountsResponse.fetching || (!tableLoaded && !isEmpty)}
      buttons={[
        {
          type: "button",
          leftIcon: <Plus size="medium" />,
          onClick: open,
          variant: "primary",
          children: "New Article",
        },
      ]}
      fullHeight
      title="Articles"
      documentTitle="Articles"
    >
      <NewArticleModal isOpen={opened} onClose={close} />

      {isEmpty ? (
        <EmptyState
          title="Create your first article"
          icon={<MagicWand size="large" />}
          buttonText="New Article"
          buttonIcon={<Plus size="medium" />}
          buttonOnClick={open}
        />
      ) : (
        <Tabs
          value={activeTab}
          className={styles.container}
          onValueChange={setActiveTab}
          tabs={[
            {
              id: "all",
              label: (
                <>
                  <span>All</span>
                  <Badge
                    showDot={false}
                    variant="badge"
                    color={activeTab === "all" ? "primary" : "default"}
                    className={styles.badge}
                  >
                    <span>
                      {articleCountsResponse.data?.articleCounts.total}
                    </span>
                  </Badge>
                </>
              ),
              content: (
                <ArticleTable
                  filterGroups={filterGroups}
                  deleteFilter={deleteFilter}
                  addFilter={addFilter}
                  editFilter={editFilter}
                  clearFilters={clearFilters}
                  onLoad={() => setTableLoaded(true)}
                />
              ),
            },
            {
              id: "generated",
              label: (
                <>
                  <span>Generated</span>
                  <Badge
                    showDot={false}
                    variant="badge"
                    color={activeTab === "generated" ? "primary" : "default"}
                    className={styles.badge}
                  >
                    <span>
                      {articleCountsResponse.data?.articleCounts.generated}
                    </span>
                  </Badge>
                </>
              ),
              content: (
                <ArticleTable
                  status={ArticleStatus.Generated}
                  filterGroups={filterGroups}
                  deleteFilter={deleteFilter}
                  addFilter={addFilter}
                  editFilter={editFilter}
                  clearFilters={clearFilters}
                  onLoad={() => setTableLoaded(true)}
                />
              ),
            },
            {
              id: "drafted",
              label: (
                <>
                  <span>Drafted</span>
                  <Badge
                    showDot={false}
                    variant="badge"
                    color={activeTab === "drafted" ? "primary" : "default"}
                    className={styles.badge}
                  >
                    <span>
                      {articleCountsResponse.data?.articleCounts.draft}
                    </span>
                  </Badge>
                </>
              ),
              content: (
                <ArticleTable
                  status={ArticleStatus.Draft}
                  filterGroups={filterGroups}
                  deleteFilter={deleteFilter}
                  addFilter={addFilter}
                  editFilter={editFilter}
                  clearFilters={clearFilters}
                  onLoad={() => setTableLoaded(true)}
                />
              ),
            },
            {
              id: "processing",
              label: (
                <>
                  <span>Processing</span>
                  <Badge
                    showDot={false}
                    variant="badge"
                    color={activeTab === "processing" ? "primary" : "default"}
                    className={styles.badge}
                  >
                    <span>
                      {articleCountsResponse.data?.articleCounts.processing}
                    </span>
                  </Badge>
                </>
              ),
              content: (
                <ArticleTable
                  status={ArticleStatus.Processing}
                  filterGroups={filterGroups}
                  deleteFilter={deleteFilter}
                  addFilter={addFilter}
                  editFilter={editFilter}
                  clearFilters={clearFilters}
                  onLoad={() => setTableLoaded(true)}
                />
              ),
            },
            {
              id: "published",
              label: (
                <>
                  <span>Published</span>
                  <Badge
                    showDot={false}
                    variant="badge"
                    color={activeTab === "published" ? "primary" : "default"}
                    className={styles.badge}
                  >
                    <span>
                      {articleCountsResponse.data?.articleCounts.published}
                    </span>
                  </Badge>
                </>
              ),
              content: (
                <ArticleTable
                  status={ArticleStatus.Published}
                  filterGroups={filterGroups}
                  deleteFilter={deleteFilter}
                  addFilter={addFilter}
                  editFilter={editFilter}
                  clearFilters={clearFilters}
                  onLoad={() => setTableLoaded(true)}
                />
              ),
            },
          ]}
        />
      )}
    </Page>
  );
}

export default Articles;
